import { useMemo, type ReactNode } from 'react';

/**
 * 해석 본문의 **강조** 표기를 실제 굵은 글씨로 렌더한다.
 *
 * 모델이 "**한 줄 요약 —**"처럼 마크다운 강조를 섞어 쓴다. 그대로 두면 별표가
 * 글자로 보인다. 프롬프트로 금지하는 대신 렌더러에서 해석하기로 한 이유는,
 * 모델이 강조하려 한 부분이 실제로 읽는 사람에게 중요한 지점이기 때문이다.
 *
 * 스트리밍 중에는 닫는 `**`가 아직 안 온 상태가 자연스럽게 생긴다.
 * 여는 표시부터 바로 굵게 그려서, 뒤늦게 굵어지며 글자가 흔들리지 않게 한다.
 */

const MARKER = '**';

export interface TextSegment {
  text: string;
  bold: boolean;
}

/** `**`를 기준으로 굵기 구간을 나눈다. 홀수 개면 마지막 구간은 "작성 중인 강조"로 본다 */
export function parseEmphasis(text: string): TextSegment[] {
  return text
    .split(MARKER)
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((s) => s.text.length > 0);
}

/** 강조 구간을 살려 텍스트를 그린다 (스트리밍이 끝난 본문·저장된 본문 공용) */
export function RichText({ text }: { text: string }) {
  const segments = useMemo(() => parseEmphasis(text), [text]);
  return (
    <>
      {segments.map((s, i) =>
        s.bold ? <Emphasis key={i}>{s.text}</Emphasis> : <span key={i}>{s.text}</span>,
      )}
    </>
  );
}

/** 강조 표기의 겉모습 — 본문과 같은 색에 굵기만 올린다 (금색은 링크·버튼용이라 피함) */
export function Emphasis({ children }: { children: ReactNode }) {
  return <b className="font-semibold text-moon">{children}</b>;
}
